import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        email: formData.email,
        password: formData.password
      });
      // Phase 0 — admin token now in HttpOnly cookie set by the server.
      // Profile retained for UI rendering of the admin dashboard.
      // Server returns the admin profile under `admin` (not `user`); guard
      // against undefined so we never write the literal string "undefined"
      // into localStorage (JSON.parse would then crash on dashboard load).
      const adminProfile = response.data.admin || response.data.user || { email: formData.email, role: 'admin' };
      localStorage.setItem('mojo_admin_user', JSON.stringify(adminProfile));
      toast.success('Admin login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B0C34] via-[#2D1854] to-[#1B0C34] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#F2B10C] rounded-full flex items-center justify-center">
            <Shield size={32} className="text-[#1B0C34]" />
          </div>
          <CardTitle className="text-2xl text-[#1B0C34]">Mojo Money Admin</CardTitle>
          <CardDescription>Analytics Dashboard Login</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="admin_email">Email</Label>
              <Input
                id="admin_email"
                type="email"
                placeholder="admin@mojomoney.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="admin_password">Password</Label>
              <Input
                id="admin_password"
                type="password"
                placeholder="Enter admin password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
              data-testid="admin-login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login as Admin'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#145539] hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Admin Password Reset</DialogTitle>
            <DialogDescription>
              Admin credentials are managed by the system administrator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Admin account passwords are configured via server environment variables and cannot be self-reset through this portal.
            </p>
            <p className="text-sm text-gray-600">
              To reset the admin password, please contact your system administrator or update the <span className="font-mono text-xs bg-gray-100 px-1 rounded">ADMIN_PASSWORD</span> environment variable on the server.
            </p>
            <Button
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              onClick={() => setShowForgotPassword(false)}
            >
              Understood
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogin;
